import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';

import { HrDayValidation } from '@app/shared/_models/hr';
import { EffectiveSlot, deltaLabel, hoursLabel, minutesLabel } from '../../hr-schedule-engine';
import { HrTimeSource } from '../../hr-payroll';
import { WorkPeriod } from '../../hr-timesheet';
import { DayValidationRow, buildDayValidationRows, changedValidationRows, dayValidationTotals, paidHoursOf, proposedPaidMinutes, setPaidHours } from '../../hr-validation';
import { HR_DAY_LABELS } from '../../schedule/schedule.component';

// Validation journalière des heures — l'écran « Journée ».
// Trois couches : PRÉVU (planning) · BADGÉ (pointeuse) · RÉMUNÉRÉ (décision du gestionnaire).
// Seul le rémunéré part en paie ; c'est la seule colonne éditable.
// `source` décide de ce qui est PROPOSÉ dans cette colonne — cf. hr-validation.ts.
export interface DayValidationDialogData {
	id_employee: number;
	name: string;
	from: string;
	to: string;
	periodLabel: string;
	slots: EffectiveSlot[];
	periods: WorkPeriod[];
	validations: HrDayValidation[];
	source?: HrTimeSource;
}

@Component({
	selector: 'app-hr-day-validation-dialog',
	standalone: true,
	imports: [FormsModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatButtonModule,
		MatIconModule, MatCheckboxModule, MatTooltipModule],
	templateUrl: './day-validation-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './day-validation-dialog.component.scss',
})
export class DayValidationDialogComponent {

	public data: DayValidationDialogData = inject(MAT_DIALOG_DATA);
	private dialogRef = inject(MatDialogRef<DayValidationDialogComponent>);

	public rows: DayValidationRow[] = [];
	public dayLabels = HR_DAY_LABELS;
	public source: HrTimeSource = this.data.source ?? 'planning';

	constructor() {
		this.rows = buildDayValidationRows(
			this.data.id_employee, this.data.from, this.data.to,
			this.data.slots, this.data.periods, this.data.validations, this.source);
	}

	// Sans badgeuse, la colonne « badgé » n'aurait que des tirets : on la retire.
	get showBadged(): boolean {
		return this.source === 'clock';
	}

	get totals() {
		return dayValidationTotals(this.rows);
	}

	h(min: number): string {
		return hoursLabel(min);
	}

	// Saisie en heures décimales (7,5) plutôt qu'en minutes : c'est ainsi qu'on raisonne en paie.
	paidHours(row: DayValidationRow): number {
		return paidHoursOf(row);
	}

	onPaidHoursChange(row: DayValidationRow, value: any): void {
		setPaidHours(row, value);
	}

	dayLabel(row: DayValidationRow): string {
		return this.dayLabels[row.weekday - 1].slice(0, 3) + ' ' + row.dat.slice(8, 10) + '/' + row.dat.slice(5, 7);
	}

	deltaLabel(row: DayValidationRow): string {
		return deltaLabel(row.delta_min);
	}

	// Reprendre la proposition d'origine — celle du mode de décompte de l'établissement.
	onReset(row: DayValidationRow): void {
		row.paid_min = proposedPaidMinutes(row.planned_min, row.badged_min, this.source);
		row.delta_min = row.paid_min - row.planned_min;
	}

	onValidateAll(): void {
		for (const r of this.rows) r.validated = true;
	}

	// On ne renvoie QUE ce qui a bougé : valider un mois déjà arbitré ne doit pas reposter
	// trente events identiques.
	onSave(): void {
		this.dialogRef.close(changedValidationRows(this.rows, this.data.validations, this.data.id_employee));
	}

	onCancel(): void {
		this.dialogRef.close(undefined);
	}

	protected readonly minutesLabel = minutesLabel;
}
